/**
 * ═══════════════════════════════════════════════════════════════
 * SAT Generator 3.0 — MathQuestionCard
 * Карточка вопроса SAT Math с KaTeX-рендерингом + NativeGraphRenderer
 *
 * PR-D4-018-FIX: Двухфазный сабмит.
 * PR-D4-020: Native Vector Renderer + KaTeX Normalization + Rationale.
 * PR-D4-023: Explanation letter sync at render time (definitive fix).
 * ═══════════════════════════════════════════════════════════════
 */

import { useCallback, useState } from 'react';
import type { Question, LocalizedText } from '../lib/types';
import { KaTeXRenderer } from './KaTeXRenderer';
import { DesmosViewer } from './DesmosViewer';

interface MathQuestionCardProps {
    question: Question;
    questionNumber: number;
    totalQuestions: number;
    selectedOption: number | null;
    onSelectOption: (optionIndex: number) => void;
    onSubmit: () => void;
    showResult?: boolean;
}

const OPTION_LABELS = ['A', 'B', 'C', 'D'];

/**
 * PR-D4-023: Sanitize explanation text at render time to reflect the correct
 * option letter shown by the green checkmark. This is the DEFINITIVE fix —
 * applied DIRECTLY to the final string before rendering, ensuring the
 * explanation always matches the visual feedback regardless of data source.
 *
 * PR-D4-025: Extended to support Russian concluding patterns:
 *   "Следовательно/Таким образом/Поэтому/Значит, правильный ответ/вариант — X"
 *   "верный ответ X"
 *
 * Uses capture groups to auto-detect language and apply appropriate replacement.
 */
export function sanitizeExplanationText(text: string, correctLetter: string): string {
    const combinedPattern = new RegExp(
        // Group 1: EN full-sentence (Accordingly/Therefore/Thus/Hence/So, the correct choice/answer/option is X)
        '((?:(?:Accordingly|Therefore|Thus|Hence|So),?\\s+)?' +
        '(?:The\\s+)?correct\\s+(?:choice|answer|option)\\s+is\\s+[A-D]\\.?)' +
        '|' +
        // Group 2: EN inverted (choice/option X is correct)
        '((?:choice|option)\\s+[A-D]\\s+is\\s+correct\\.?)' +
        '|' +
        // Group 3: RU (Следовательно/Таким образом/Поэтому/Значит, правильный ответ/вариант/верный ответ X)
        '((?:(?:Следовательно|Таким\\s+образом|Поэтому|Значит),?\\s+)?' +
        '(?:правильный\\s+(?:ответ|вариант)|верный\\s+ответ)\\s*[-—:]?' +
        '\\s*[A-DА-Г]\\.?)',
        'gi',
    );

    return text.replace(combinedPattern, function (_match, _enFull, _enInverted, ru) {
        if (ru !== undefined) {
            return `Следовательно, правильный вариант — ${correctLetter}.`;
        }
        return `Therefore, the correct choice is ${correctLetter}.`;
    });
}

/**
 * PR-D4-025: Extended with `lang` parameter to support language selection.
 * When `lang='ru'`, prefers Russian translation first, falls back to English.
 */
function extractExplanation(
    explanation: LocalizedText | Record<string, string> | string | undefined | null,
    lang: string = 'en',
): string {
    if (!explanation) return '';
    if (typeof explanation === 'string') return explanation;
    if ('translations' in explanation && explanation.translations && typeof explanation.translations === 'object') {
        const t = explanation.translations as Record<string, string>;
        if (lang === 'ru' && t.ru) return t.ru;
        if (lang === 'en' && t.en) return t.en;
        return t.en || t.ru || Object.values(t)[0] || '';
    }
    if (typeof explanation === 'object') {
        const record = explanation as Record<string, string>;
        if (lang === 'ru' && record.ru) return record.ru;
        if (lang === 'en' && record.en) return record.en;
        return record.en || record.ru || Object.values(record)[0] || '';
    }
    return '';
}

export function MathQuestionCard({
    question,
    questionNumber,
    totalQuestions,
    selectedOption,
    onSelectOption,
    onSubmit,
    showResult = false,
}: MathQuestionCardProps) {
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent, index: number) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectOption(index);
            }
        },
        [onSelectOption],
    );

    // Фидбек показываем ТОЛЬКО когда showResult = true
    const hasFeedback = showResult && question.correct_option_index !== undefined;

    // Derive the correct/incorrect state for each option when feedback is shown
    const getOptionStyle = (index: number) => {
        if (!hasFeedback) return '';

        const isCorrect = index === question.correct_option_index;
        const isSelected = index === selectedOption;

        if (isCorrect && isSelected) return 'border-green-500 bg-green-50 ring-2 ring-green-200';
        if (isCorrect) return 'border-green-400 bg-green-50/50';
        if (isSelected) return 'border-red-400 bg-red-50 ring-2 ring-red-200';
        return '';
    };

    const graphObj = question.graph_data as unknown as Record<string, unknown> | null;
    const svgFallback =
        question.svg_data ||
        (graphObj?.svg as string | null) ||
        null;

    const qUnknown = question as unknown as Record<string, unknown>;

    // PR-D4-025: Explanation language state with auto-detection
    const explanationData: LocalizedText | Record<string, string> | string | undefined | null =
        question.explanation || (qUnknown.explanations as Record<string, string>);
    const hasRussian = typeof explanationData === 'object' && explanationData !== null
        ? ('translations' in explanationData
            ? !!(explanationData as { translations: Record<string, string> }).translations?.ru
            : !!(explanationData as Record<string, string>).ru)
        : false;
    const [explanationLang, setExplanationLang] = useState<'en' | 'ru'>(
        hasRussian ? 'ru' : 'en',
    );

    // PR-D4-025: Extract explanation in active language
    const rawExplanationText = extractExplanation(explanationData, explanationLang);
    // PR-D4-023: Derive correct letter from the (shuffled) correct_option_index
    const correctLetter = OPTION_LABELS[question.correct_option_index] ?? 'A';
    // PR-D4-023: Sanitize the explanation at render time — definitive fix
    const explanationText = sanitizeExplanationText(rawExplanationText, correctLetter);

    return (
        <div className="space-y-4">
            {/* Question Header */}
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-500">
                    Question {questionNumber} of {totalQuestions}
                </span>
            </div>

            {/* Question Card */}
            <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-6">
                {/* Question Text (KaTeX) */}
                <div className="text-base leading-relaxed text-gray-900">
                    <KaTeXRenderer text={question.question_text} />
                </div>

                {/* Graph / Coordinate plane — Native Vector Renderer */}
                {(question.graph_data || svgFallback) && (
                    <div className="mt-4 mx-auto w-full max-w-[480px] max-h-[480px] aspect-square flex items-center justify-center p-2 overflow-hidden">
                        <DesmosViewer
                            graphData={question.graph_data}
                            svgFallback={svgFallback}
                            height={480}
                            width="100%"
                        />
                    </div>
                )}

                {/* Options */}
                <fieldset className="mt-4 space-y-2">
                    <legend className="sr-only">Answer options</legend>
                    {question.options.map((option, index) => {
                        const isSelected = selectedOption === index;
                        const feedbackStyle = getOptionStyle(index);

                        return (
                            <div
                                key={index}
                                role="radio"
                                aria-checked={isSelected}
                                tabIndex={0}
                                onClick={() => onSelectOption(index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-all ${
                                    isSelected
                                        ? 'border-gray-400 bg-gray-50'
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                } ${feedbackStyle}`}
                            >
                                {/* Option Letter */}
                                <span
                                    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-xs font-bold transition-all ${
                                        isSelected
                                            ? 'bg-gray-800 text-white'
                                            : 'bg-gray-100 text-gray-600'
                                    }`}
                                >
                                    {OPTION_LABELS[index]}
                                </span>

                                {/* Option Text (KaTeX) */}
                                <div className="flex-1 text-sm text-gray-800">
                                    <KaTeXRenderer text={option} />
                                </div>
                            </div>
                        );
                    })}
                </fieldset>

                {/* Solution / Rationale (when feedback is revealed) */}
                {hasFeedback && explanationText && (
                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                        <div className="mb-1 flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Explanation</span>
                            {/* PR-D4-025: Language Toggle — only show if Russian data exists */}
                            {hasRussian && (
                                <span className="ml-auto flex items-center gap-1 text-xs">
                                    <button
                                        onClick={() => setExplanationLang('en')}
                                        className={`px-2 py-0.5 rounded border ${
                                            explanationLang === 'en'
                                                ? 'border-gray-400 bg-gray-200 text-gray-800'
                                                : 'border-gray-200 text-gray-500 hover:bg-gray-100'
                                        }`}
                                    >
                                        EN
                                    </button>
                                    <button
                                        onClick={() => setExplanationLang('ru')}
                                        className={`px-2 py-0.5 rounded border ${
                                            explanationLang === 'ru'
                                                ? 'border-gray-400 bg-gray-200 text-gray-800'
                                                : 'border-gray-200 text-gray-500 hover:bg-gray-100'
                                        }`}
                                    >
                                        RU
                                    </button>
                                </span>
                            )}
                        </div>
                        <div className="leading-relaxed">
                            <KaTeXRenderer text={explanationText} />
                        </div>
                    </div>
                )}

                <button
                    onClick={onSubmit}
                    disabled={selectedOption === null}
                    className="mt-4 w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                >
                    {selectedOption !== null ? 'Submit Answer →' : 'Select an option to continue'}
                </button>
            </div>
        </div>
    );
}

export default MathQuestionCard;